import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Borrower } from 'app/models/dto/borrower.model';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { BorrowerService } from 'app/services/borrower/borrower.service';
import { ClaimLeadService, ClaimRestrictions } from 'app/services/claim-lead/claim-lead.service';
import { environment } from 'environments/environment';
import { of } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { ClaimAssignment, GetClaimRestrictions, ResetConflict, SetFlagHighPriority, SetNextAssignment, SubscribeToJumpballNotify, SubscribeToUnattemptedLeads, UnsubscribeFromJumpballNotify, UnsubscribeFromUnattemptedLeads } from './claim-lead.actions';

interface LenderUsersWithLeads {
  [lenderUserId: number]: {
    lenderUserId: number;
    isHighPriority: number;
  };
}

export class ClaimLeadStateModel {
  claimRestrictions: ClaimRestrictions | null;
  flagHighPriority: boolean;
  nextAssignmentExists: boolean;
  loading: boolean;
  claimedLead: Borrower | null;
  conflict: boolean;
}

@Injectable()
@State<ClaimLeadStateModel>({
  name: 'claimLead',
  defaults: {
    nextAssignmentExists: false,
    flagHighPriority: false,
    claimRestrictions: null,
    claimedLead: null,
    loading: false,
    conflict: false
  }
})

export class ClaimLeadState {

  @Selector()
  static nextAssignmentExists(state: ClaimLeadStateModel) {
    return state.nextAssignmentExists;
  }

  @Selector()
  static flagHighPriority(state: ClaimLeadStateModel) {
    return state.flagHighPriority;
  }

  @Selector()
  static claimRestrictions(state: ClaimLeadStateModel) {
    return state.claimRestrictions;
  }

  @Selector()
  static claimedLead(state: ClaimLeadStateModel) {
    return state.claimedLead;
  }

  @Selector()
  static loading(state: ClaimLeadStateModel) {
    return state.loading;
  }

  @Selector()
  static conflict(state: ClaimLeadStateModel) {
    return state.conflict;
  }

  constructor(
    private claimLeadService: ClaimLeadService,
    private pusherService: PusherService,
    private store: Store,
    private borrowerService: BorrowerService
  ) { }

  @Action(SubscribeToUnattemptedLeads)
  subscribeToUnattemptedLeads({}: StateContext<ClaimLeadStateModel>, { lenderUser }: SubscribeToUnattemptedLeads) {
    this.pusherSubscribeToUnattemptedLeads(lenderUser);
  }

  @Action(UnsubscribeFromUnattemptedLeads)
  unsubscribeFromUnattemptedLeads({}: StateContext<ClaimLeadStateModel>, { userId }: UnsubscribeFromUnattemptedLeads) {
    this.pusherUnsubscribeFromUnattemptedLeads(userId);
  }

  @Action(SubscribeToJumpballNotify)
  SubscribeToJumpballNotify({ }: StateContext<ClaimLeadStateModel>, { lenderUser }: SubscribeToJumpballNotify) {
    this.pusherSubscribeToJumpballNotify(lenderUser);
  }

  @Action(UnsubscribeFromJumpballNotify)
  unsubscribeFromJumpballNotify({}: StateContext<ClaimLeadStateModel>, { lenderUser }: UnsubscribeFromJumpballNotify) {
    this.pusherUnsubscribeFromJumpballNotify(lenderUser);
  }

  @Action(SetNextAssignment)
  setNextAssignment({ patchState }: StateContext<ClaimLeadStateModel>, { val }: SetNextAssignment) {
    patchState({ nextAssignmentExists: val });
  }

  @Action(SetFlagHighPriority)
  setFlagHighPriority({ patchState }: StateContext<ClaimLeadStateModel>, { val }: SetFlagHighPriority) {
    patchState({ flagHighPriority: val });
  }

  @Action(GetClaimRestrictions)
  getClaimRestrictions({ patchState }: StateContext<ClaimLeadStateModel>, { }: GetClaimRestrictions) {
    patchState({ loading: true });
    return this.claimLeadService.getClaimRestrictions().pipe(
      tap(claimRestrictions => patchState({ claimRestrictions, loading: false }))
    );
  }

  @Action(ResetConflict)
  resetConflict({ patchState }: StateContext<ClaimLeadStateModel>, {}: ResetConflict) {
    patchState({ conflict: false });
  }

  @Action(ClaimAssignment)
  claimAssignment({ patchState }: StateContext<ClaimLeadStateModel>, {}: ClaimAssignment) {
    patchState({ loading: true });
    return this.claimLeadService.claimAssignment().pipe(tap(assignment => {
      return this.borrowerService.get(assignment.data.borrowerId).pipe(take(1), tap(borrower => {
        patchState({
          loading: false,
          claimedLead: borrower,
          nextAssignmentExists: false
        });
      })).subscribe();
    }), catchError(err => {
      if (err.status === 409) {
        patchState({
          conflict: true,
          nextAssignmentExists: false,
          loading: false
        });
        return of(null);
      }
      if (err.status === 400) {
        if (err?.error?.errors?.claimRestrictions) {
          patchState({
            claimRestrictions: err.error.errors.claimRestrictions,
            loading: false
          });
        }
        return of(null);
      }
      patchState({ loading: false });
      return of();
    }));
  }

  private pusherSubscribeToUnattemptedLeads(lenderUser: LenderUser) {
    this.pusherService
      .subscribeRaw(environment.environment + '-lender-user-' + lenderUser.id + '-unattempted-leads-channel')
      .bind_global((eventName: string) => {
        if (!eventName.startsWith('pusher:')) {
          this.store.dispatch(new GetClaimRestrictions());
        }
    });
  }
  
  private pusherUnsubscribeFromUnattemptedLeads(userId: number) {
    this.pusherService.unsubscribeRaw(environment.environment + '-lender-user-' + userId + '-unattempted-leads-channel');
  }

  private pusherSubscribeToJumpballNotify(lenderUser: LenderUser) {
    this.pusherService
      .subscribeRaw('private-lender-' + lenderUser.institution_id + '-' + environment.environment + '-jumpball-notify-channel')
      .bind_global((eventName: string, lenderUsersWithLeads: LenderUsersWithLeads) => {
        if (!eventName.startsWith('pusher:')) {
          if (this.userIdInJumpballNotify(lenderUser.id, lenderUsersWithLeads)) {
            this.store.dispatch(new SetNextAssignment(true));

            this.store.dispatch(new SetFlagHighPriority(
              Boolean(lenderUsersWithLeads[lenderUser.id].isHighPriority)
            ));
          } else {
            this.store.dispatch(new SetNextAssignment(false));
          }
        }
    });
  }

  private pusherUnsubscribeFromJumpballNotify(lenderUser: LenderUser) {
    this.pusherService
      .unsubscribeRaw('private-lender-' + lenderUser.institution_id + '-' + environment.environment + '-jumpball-notify-channel');
  }

  private userIdInJumpballNotify(lenderUserId: number, lenderUsersWithLeads: LenderUsersWithLeads): boolean {
    return lenderUsersWithLeads.hasOwnProperty(lenderUserId);
  }

}
